import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  Container, Paper, Typography, Box, Button, TextField, MenuItem, Alert,
  CircularProgress, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, Grid, Select, FormControl, InputLabel, Divider,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  EventBusy as EventBusyIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '/api';

const extractId = (value) => {
  if (!value) return '';
  if (typeof value === 'string' || typeof value === 'number') return String(value).trim();
  if (typeof value === 'object') {
    return String(value._id || value.id || value.niveau_id || value.filiere_id || value.annee_id || '').trim();
  }
  return '';
};

const compareIds = (id1, id2) => {
  const left = extractId(id1);
  const right = extractId(id2);
  return Boolean(left && right && left === right);
};

const toFr = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? '—' : d.toLocaleDateString('fr-FR');
};

const SuiviAbsences = () => {
  const [annees, setAnnees] = useState([]);
  const [filieres, setFilieres] = useState([]);
  const [niveauxFiltres, setNiveauxFiltres] = useState([]);
  const [classesRef, setClassesRef] = useState([]);
  const [etudiants, setEtudiants] = useState([]);
  const [inscriptions, setInscriptions] = useState([]);

  const [filters, setFilters] = useState({ annee_universitaire_id: '', filiere_id: '', niveau_id: '' });

  const [loading, setLoading] = useState(false);
  const [effLoading, setEffLoading] = useState(false);
  const [error, setError] = useState('');

  const [classesNiveau, setClassesNiveau] = useState([]);
  const [presencesNiveau, setPresencesNiveau] = useState([]);
  const [seanceById, setSeanceById] = useState(new Map());

  const [selectedId, setSelectedId] = useState('');

  const loadReferences = useCallback(async () => {
    try {
      const [anneesRes, filieresRes, classesRes] = await Promise.all([
        axios.get(`${API_URL}/annees-universitaires`),
        axios.get(`${API_URL}/filieres`),
        axios.get(`${API_URL}/classes`),
      ]);
      setAnnees(anneesRes.data.data || anneesRes.data || []);
      setFilieres(filieresRes.data.data || filieresRes.data || []);
      setClassesRef(classesRes.data || []);
    } catch (err) {
      setError('Erreur lors du chargement des références');
    }
  }, []);

  useEffect(() => { loadReferences(); }, [loadReferences]);

  // Preselectionner l annee active (2026/2027) a l ouverture.
  useEffect(() => {
    if (annees.length && !filters.annee_universitaire_id) {
      const actif = annees.find(a => a.estActive === true) || annees[0];
      if (actif) {
        setFilters(prev => prev.annee_universitaire_id ? prev : { ...prev, annee_universitaire_id: actif._id || actif.id });
      }
    }
  }, [annees, filters.annee_universitaire_id]);

  const loadNiveaux = useCallback(async (filiereId) => {
    if (!filiereId) { setNiveauxFiltres([]); return; }
    try {
      const r = await axios.get(`${API_URL}/filieres/${filiereId}/niveaux`);
      setNiveauxFiltres(r.data.data || r.data || []);
    } catch (e) {
      setNiveauxFiltres([]);
    }
  }, []);

  useEffect(() => {
    if (filters.filiere_id) {
      loadNiveaux(filters.filiere_id);
      setFilters(prev => (prev.niveau_id ? prev : { ...prev, niveau_id: '' }));
    } else {
      setNiveauxFiltres([]);
    }
  }, [filters.filiere_id, loadNiveaux]);

  // Resolution commune (page Etudiants) : a partir des selections, calculer les
  // ensembles d identifiants acceptes pour l annee, la filiere et le niveau.
  const resoudreFiltres = useCallback((f = filters) => {
    const a = extractId(f.annee_universitaire_id);
    const fil = extractId(f.filiere_id);
    const n = extractId(f.niveau_id);
    if (!a || !fil || !n) return null;

    const anneeDoc = annees.find(x => compareIds(x._id, a) || compareIds(x.annee_id, a));
    const filiereDoc = filieres.find(x => compareIds(x._id, fil) || compareIds(x.filiere_id, fil));
    const niveauDoc = niveauxFiltres.find(x => compareIds(x._id, n) || compareIds(x.niveau_id, n));

    const acc = (doc, biz) => [doc?._id, biz, doc?.annee_id, doc?.filiere_id, doc?.niveau_id].map(extractId).filter(Boolean);
    return {
      a, fil, n,
      anneeDoc, filiereDoc, niveauDoc,
      allowedA: new Set(acc(anneeDoc, a)),
      allowedF: new Set(acc(filiereDoc, fil)),
      allowedN: new Set(acc(niveauDoc, n)),
    };
  }, [filters, annees, filieres, niveauxFiltres]);

  // Effectif inscrit du niveau : endpoint dedie (payload leger), jamais le dump
  // complet des inscriptions (trop volumineux en production -> timeout).
  const effectifSeq = useRef(0);

  const fetchEffectif = useCallback(async () => {
    const seq = ++effectifSeq.current;
    const refs = resoudreFiltres();
    setInscriptions([]);
    setEffLoading(true);
    if (!refs) { setEffLoading(false); return; }
    const params = new URLSearchParams();
    const aj = (set) => [...set].join(',');
    params.set('annee', aj(refs.allowedA));
    params.set('filiere', aj(refs.allowedF));
    try {
      const r = await axios.get(`${API_URL}/inscriptions/niveau/${encodeURIComponent(refs.n)}`, { params, timeout: 15000 });
      const d = r.data && (r.data.data || r.data);
      if (Array.isArray(d)) {
        if (seq === effectifSeq.current) setInscriptions(d);
        setEffLoading(false);
        return;
      }
    } catch (err) { /* repli ci-dessous (ancien backend) */ }
    // Repli : ancien backend sans endpoint dedie. Dump complet limite a l usage
    // des noms (les presences fournissent deja l effectif reellement appele).
    const liste = async (urls) => {
      for (const url of urls) {
        try {
          const r = await axios.get(`${API_URL}${url}`, { timeout: 25000 });
          const d = r.data && (r.data.data || r.data);
          if (Array.isArray(d)) return d;
        } catch (e) { /* essai suivant */ }
      }
      return [];
    };
    const [e, i] = await Promise.all([
      liste(['/etudiants', '/etudiant', '/students']),
      liste(['/inscriptions', '/inscription', '/enrollments', '/registrations']),
    ]);
    if (seq === effectifSeq.current) {
      setEtudiants(e);
      setInscriptions(i);
    }
    setEffLoading(false);
  }, [resoudreFiltres]);

  useEffect(() => {
    if (filters.annee_universitaire_id && filters.filiere_id && filters.niveau_id) {
      fetchEffectif();
    } else {
      setInscriptions([]);
      setPresencesNiveau([]);
      setSeanceById(new Map());
    }
  }, [filters.annee_universitaire_id, filters.filiere_id, filters.niveau_id, fetchEffectif]);

  // Classes du niveau (sert a chercher les seances et les presences).
  const classesDuNiveau = useCallback(() => {
    const refs = resoudreFiltres();
    if (!refs) return [];
    const { allowedA, allowedF, allowedN } = refs;
    return classesRef.filter(c => {
      // L annee est un filtre souple : une classe qui ne declare pas d annee
      // (annee_id vide) reste visible quelque soit l annee choisie.
      const cA = [c.anneeUniversitaire, c.annee_id, c.anneeId].map(extractId).some(id => !id || allowedA.has(id));
      const cF = [c.filiere, c.filiere_id, c.filiereId].map(extractId).some(id => allowedF.has(id));
      const cN = [c.niveau, c.niveau_id, c.niveauId].map(extractId).some(id => allowedN.has(id));
      return cA && cF && cN;
    });
  }, [resoudreFiltres, classesRef]);

  const chargerNiveau = useCallback(async () => {
    const classes = classesDuNiveau();
    setClassesNiveau(classes);
    setSelectedId('');
    setError('');
    if (classes.length === 0) {
      setPresencesNiveau([]);
      setSeanceById(new Map());
      return;
    }
    setLoading(true);
    try {
      // Les presences et les seances stockent le classe_id sous la forme du
      // numero metier ("*1") OU de l ObjectId hexa : on recupere les seances
      // et les presences pour chaque valeur candidate.
      const candidats = [];
      classes.forEach(c => { candidats.push(String(c.id)); candidats.push(String(c._id)); });
      const uniques = [...new Set(candidats.filter(Boolean))];

      const requetes = uniques.map(k => axios.get(`${API_URL}/presences/seances/classe/${k}`, { timeout: 12000 }).then(r => r.data || []).catch(() => []));
      const requetesPres = uniques.map(k => axios.get(`${API_URL}/presences/classe/${k}`, { timeout: 12000 })
        .then(r => ({ ok: true, data: r.data || [] }))
        .catch(() => ({ ok: false })));
      const [seancesRes, classesPresRes] = await Promise.all([Promise.all(requetes), Promise.all(requetesPres)]);

      const seances = seancesRes.flat();
      const map = new Map();
      seances.forEach(s => { if (s && s._id) map.set(String(s._id), s); });
      setSeanceById(map);

      // Si l'endpoint par classe est disponible, on ne charge que les presences
      // du niveau. Sinon (backend pas encore redeploye), repli sur le dump complet.
      let ciblées;
      if (classesPresRes.some(r => r.ok)) {
        ciblées = classesPresRes.filter(r => r.ok).flatMap(r => r.data);
      } else {
        const full = (await axios.get(`${API_URL}/presences`, { timeout: 30000 })).data || [];
        ciblées = full.filter(p => p && uniques.indexOf(String(p.classe_id)) !== -1);
      }
      setPresencesNiveau(ciblées);
    } catch (e) {
      setError('Erreur lors du chargement des présences');
      setPresencesNiveau([]);
    } finally {
      setLoading(false);
    }
  }, [classesDuNiveau]);

  useEffect(() => {
    if (filters.annee_universitaire_id && filters.filiere_id && filters.niveau_id) {
      chargerNiveau();
    } else {
      setClassesNiveau([]);
      setPresencesNiveau([]);
      setSeanceById(new Map());
      setSelectedId('');
    }
  }, [filters.annee_universitaire_id, filters.filiere_id, filters.niveau_id, chargerNiveau]);

  const handleFilter = (name) => (e) => {
    const value = e.target.value;
    setFilters(prev => ({ ...prev, [name]: value, ...(name === 'filiere_id' ? { niveau_id: '' } : {}) }));
  };

  // Inscriptions du niveau selectionne (meme resolution que la page Etudiants).
  const inscriptionsNiveau = useMemo(() => {
    const refs = resoudreFiltres();
    if (!refs) return [];
    const { allowedA, allowedF, allowedN } = refs;
    return inscriptions.filter(ins => {
      const okA = [...allowedA].some(id => compareIds(extractId(ins.annee_universitaire_id), id));
      const okF = [...allowedF].some(id => compareIds(extractId(ins.filiere_id), id));
      const okN = [...allowedN].some(id => compareIds(extractId(ins.niveau_id), id));
      return okA && okF && okN;
    });
  }, [inscriptions, resoudreFiltres]);

  // Etudiants du niveau : les inscrits (meme avec 0 absence) + ceux vus dans les
  // appels, chacun avec son etat (total, presents, absences, justifiees).
  const listeEtudiants = useMemo(() => {
    const map = new Map();
    const etuParId = new Map();
    etudiants.forEach(e => etuParId.set(String(extractId(e._id || e.id)), e));

    const nomDe = (ins) => (ins && (ins.etudiant?.Nom || ins.etudiant?.nom || ins.etudiant?.lastName)) || (ins && ins.etudiant_nom) || '';
    const prenomDe = (ins) => (ins && (ins.etudiant?.Prénom || ins.etudiant?.prenom || ins.etudiant?.prénom || ins.etudiant?.firstName)) || (ins && ins.etudiant_prenom) || '';
    const cneDe = (ins) => (ins && (ins.etudiant?.CNE || ins.etudiant?.cne || ins.etudiant?.numeroCne)) || '';

    inscriptionsNiveau.forEach(ins => {
      const eid = String(extractId(ins.etudiant_id));
      const e = etuParId.get(eid);
      const nom = nomDe(ins) || (e && (e.Nom || e.nom || e.lastName)) || '';
      const prenom = prenomDe(ins) || (e && (e.Prénom || e.prenom || e.prénom || e.firstName)) || '';
      const cne = cneDe(ins) || (e && (e.CNE || e.cne || e.numeroCne)) || '';
      const id = eid || String(extractId(e && (e._id || e.id)));
      if (!map.has(id)) {
        map.set(id, { id, nom, prenom, cne, total: 0, presents: 0, absences: 0, justifiees: 0 });
      }
    });

    presencesNiveau.forEach(p => {
      const id = String(p.etudiant_id);
      let ligne = map.get(id);
      if (!ligne) {
        ligne = { id, nom: p.etudiant_nom, prenom: p.etudiant_prenom, cne: p.etudiant_cne || '', total: 0, presents: 0, absences: 0, justifiees: 0 };
        map.set(id, ligne);
      }
      ligne.total++;
      if (p.present) ligne.presents++;
      else {
        ligne.absences++;
        if (p.justifie) ligne.justifiees++;
      }
    });

    return [...map.values()].sort((a, b) => b.absences - a.absences || (a.nom || '').localeCompare(b.nom || ''));
  }, [inscriptionsNiveau, etudiants, presencesNiveau]);

  const selection = listeEtudiants.find(e => e.id === selectedId) || null;

  const absencesEtudiant = selection
    ? presencesNiveau.filter(p => String(p.etudiant_id) === selection.id && !p.present).sort((a, b) => new Date(a.date) - new Date(b.date))
    : [];

  const parMatiere = (() => {
    const map = new Map();
    absencesEtudiant.forEach(p => {
      const cle = p.cours_nom || '—';
      map.set(cle, (map.get(cle) || 0) + 1);
    });
    return [...map.entries()].sort((a, b) => b[1] - a[1]);
  })();

  const renderFiche = (seanceId) => seanceById.get(String(seanceId));

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Paper sx={{ p: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">
            Suivi des absences des étudiants
            <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 2 }}>
              {listeEtudiants.length} étudiant(s) · {presencesNiveau.length} présences
            </Typography>
          </Typography>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={chargerNiveau} disabled={!filters.niveau_id || loading}>
            Actualiser
          </Button>
        </Box>

        {/* Filtres en cascade : annee -> filiere -> niveau */}
        <Grid container spacing={2} mb={3}>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="au-label">Année universitaire</InputLabel>
              <Select labelId="au-label" label="Année universitaire" value={filters.annee_universitaire_id} onChange={handleFilter('annee_universitaire_id')}>
                <MenuItem value="">Toutes les années</MenuItem>
                {annees.map(a => (
                  <MenuItem key={a._id || a.id} value={a._id || a.id}>
                    {a.intitule || a.annee || `${a.anneeDebut}–${a.anneeFin}`} {a.estActive ? '· Active' : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="f-label">Filière</InputLabel>
              <Select labelId="f-label" label="Filière" value={filters.filiere_id} onChange={handleFilter('filiere_id')} disabled={!filters.annee_universitaire_id}>
                <MenuItem value="">Toutes les filières</MenuItem>
                {filieres.map(fil => (
                  <MenuItem key={fil._id || fil.id} value={fil._id || fil.id}>
                    {fil.intitule || fil.nom || fil.name || fil.filiere_id}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={4}>
            <FormControl fullWidth>
              <InputLabel id="n-label">Niveau</InputLabel>
              <Select labelId="n-label" label="Niveau" value={filters.niveau_id} onChange={handleFilter('niveau_id')} disabled={!filters.filiere_id}>
                <MenuItem value="">Tous les niveaux</MenuItem>
                {niveauxFiltres.map(nv => (
                  <MenuItem key={nv._id || nv.id || nv.niveau_id} value={nv._id || nv.id || nv.niveau_id}>
                    {nv.intitule || nv.code || nv.nom}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

        {!filters.niveau_id && (
          <Box textAlign="center" py={6} color="text.secondary">
            <EventBusyIcon sx={{ fontSize: 56, mb: 2, color: '#cbd5e1' }} />
            <Typography>Choisissez une année, une filière puis un niveau pour voir les absences.</Typography>
          </Box>
        )}

        {filters.niveau_id && classesNiveau.length === 0 && !loading && listeEtudiants.length > 0 && (
          <Box textAlign="center" py={2} color="text.secondary">
            <Typography variant="body2">Aucune séance de cours déclarée pour ce niveau (les absences s'afficheront après les appels).</Typography>
          </Box>
        )}

        {(loading || effLoading) && (
          <Box textAlign="center" py={6}><CircularProgress /></Box>
        )}

        {!loading && !effLoading && filters.niveau_id && listeEtudiants.length === 0 && (
          <Box textAlign="center" py={6} color="text.secondary">
            <Typography>Aucun étudiant inscrit ni d'appel enregistré pour ce niveau.</Typography>
          </Box>
        )}

        {!loading && !effLoading && listeEtudiants.length > 0 && (
          <Grid container spacing={3}>
            {/* Liste des etudiants du niveau */}
            <Grid item xs={12} md={5} lg={4}>
              <Paper variant="outlined" sx={{ p: 1 }}>
                <Box px={1.5} py={1}>
                  <TextField
                    size="small"
                    fullWidth
                    placeholder="Rechercher un étudiant…"
                    InputProps={{ startAdornment: <SearchIcon sx={{ mr: 1, color: '#9ca3af' }} /> }}
                    onChange={(e) => {
                      const q = (e.target.value || '').toLowerCase();
                      const premier = q ? listeEtudiants.find(st => (st.nom + ' ' + st.prenom + ' ' + st.cne).toLowerCase().includes(q)) : null;
                      if (premier) setSelectedId(premier.id);
                    }}
                  />
                </Box>
                <Box sx={{ maxHeight: 520, overflowY: 'auto', mt: 1 }}>
                  {listeEtudiants.map(st => (
                    <Box
                      key={st.id}
                      onClick={() => setSelectedId(st.id)}
                      sx={{
                        px: 1.5, py: 1, cursor: 'pointer', borderRadius: 1, mx: 0.5, mb: 0.5,
                        background: st.id === selectedId ? '#eef2ff' : 'transparent',
                        borderLeft: st.id === selectedId ? '4px solid #6366f1' : '4px solid transparent',
                      }}
                    >
                      <Box display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                        <Box minWidth={0}>
                          <Typography variant="body2" noWrap>
                            <PersonIcon sx={{ fontSize: 15, verticalAlign: 'middle', mr: 0.5, color: '#94a3b8' }} />
                            <strong>{st.nom}</strong> {st.prenom}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">{st.cne || 'CNE : —'}</Typography>
                        </Box>
                        <Chip
                          size="small"
                          label={`${st.absences} abs`}
                          color={st.absences > 0 ? 'error' : 'success'}
                          variant={st.absences > 0 ? 'filled' : 'outlined'}
                        />
                      </Box>
                    </Box>
                  ))}
                </Box>
              </Paper>
            </Grid>

            {/* Etat d absences de l etudiant selectionne */}
            <Grid item xs={12} md={7} lg={8}>
              {!selection ? (
                <Box textAlign="center" py={6} color="text.secondary">
                  <Typography>Sélectionnez un étudiant pour consulter son état d'absences.</Typography>
                </Box>
              ) : (
                <>
                  <Paper variant="outlined" sx={{ p: 2, mb: 2, background: '#f8fafc' }}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                      <Box>
                        <Typography variant="h6">{selection.nom} {selection.prenom}</Typography>
                        <Typography variant="body2" color="text.secondary">{selection.cne || 'CNE : —'}</Typography>
                      </Box>
                      <Box display="flex" gap={1} flexWrap="wrap">
                        <Chip label={`Présences : ${selection.presents}`} color="success" variant="outlined" />
                        <Chip label={`Absences : ${selection.absences}`} color="error" />
                        <Chip label={`Justifiées : ${selection.justifiees}`} color="info" variant="outlined" />
                        <Chip label={`Taux de présence : ${selection.total ? ((selection.presents + selection.justifiees) / selection.total * 100).toFixed(1) : 0}%`} />
                      </Box>
                    </Box>
                  </Paper>

                  {/* Statistiques par matiere */}
                  <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>Absences par matière</Typography>
                    {parMatiere.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Aucune absence enregistrée.</Typography>
                    ) : (
                      <Box>
                        {parMatiere.map(([matiere, nb], i) => (
                          <Box key={matiere} sx={{ mb: 1 }}>
                            <Box display="flex" justifyContent="space-between" mb={0.5}>
                              <Typography variant="body2">{matiere}</Typography>
                              <Typography variant="body2" fontWeight={700}>{nb}</Typography>
                            </Box>
                            <Box sx={{ height: 8, background: '#f1f5f9', borderRadius: 4, overflow: 'hidden' }}>
                              <Box sx={{ width: `${Math.min(100, Math.round(nb / parMatiere[0][1] * 100))}%`, height: '100%', background: i % 2 ? '#f97316' : '#ef4444', borderRadius: 4 }} />
                            </Box>
                          </Box>
                        ))}
                        <Divider sx={{ my: 1 }} />
                        <Typography variant="body2" fontWeight={700}>Total : <span style={{ color: '#dc2626' }}>{parMatiere.reduce((s, [, nb]) => s + nb, 0)} absences</span></Typography>
                      </Box>
                    )}
                  </Paper>

                  {/* Detail des absences */}
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 700 }}>
                      Détail des absences ({absencesEtudiant.length})
                    </Typography>
                    {absencesEtudiant.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">Aucune absence à afficher.</Typography>
                    ) : (
                      <TableContainer>
                        <Table size="small">
                          <TableHead>
                            <TableRow sx={{ background: '#f1f5f9' }}>
                              <TableCell>Date</TableCell>
                              <TableCell>Séance</TableCell>
                              <TableCell>Matière</TableCell>
                              <TableCell>Groupe</TableCell>
                              <TableCell>Justifiée</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {absencesEtudiant.map((p, i) => {
                              const fiche = renderFiche(p.seance_id);
                              return (
                                <TableRow key={`${p.seance_id}-${p.etudiant_id}-${i}`} sx={{ borderBottom: '1px solid #f1f5f9' }}>
                                  <TableCell style={{ whiteSpace: 'nowrap' }}>{toFr(p.date)}</TableCell>
                                  <TableCell style={{ whiteSpace: 'nowrap' }}>
                                    {fiche && fiche.heureDebut ? `${fiche.heureDebut} – ${fiche.heureFin}` : p.heureArrivee || '—'}
                                  </TableCell>
                                  <TableCell>{p.cours_nom || '—'}</TableCell>
                                  <TableCell>{p.classe_nom || '—'}</TableCell>
                                  <TableCell>
                                    {p.justifie
                                      ? <Chip size="small" label={p.justification || 'Justifiée'} color="secondary" />
                                      : <Chip size="small" label="Non justifiée" variant="outlined" />}
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    )}
                  </Paper>
                </>
              )}
            </Grid>
          </Grid>
        )}
      </Paper>
    </Container>
  );
};

export default SuiviAbsences;